/** Language context — provides current language and t() function.

Defaults to the config's language, with a toggle to switch between
German (de) and English (en).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { UiTextConfig } from "../types";

interface LanguageContextValue {
  lang: string;
  setLang: (lang: string) => void;
  toggleLang: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function useLanguageContext() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguageContext must be used within LanguageProvider");
  }
  return ctx;
}

interface LanguageProviderProps {
  uiText: UiTextConfig;
  children: ReactNode;
}

export function LanguageProvider({ uiText, children }: LanguageProviderProps) {
  const [lang, setLang] = useState(uiText.language || "de");

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key: string) => {
      const translations = uiText[lang as "de" | "en"];
      if (translations && key in translations) {
        return translations[key];
      }
      // Fallback to German
      if (uiText.de && key in uiText.de) {
        return uiText.de[key];
      }
      return key;
    },
    [uiText, lang],
  );

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === "de" ? "en" : "de"));
  }, []);

  const value = useMemo(
    () => ({ lang, setLang, toggleLang, t }),
    [lang, t, toggleLang],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
